/**
 * BIOS Compatibility Engine
 *
 * Checks BIOS compatibility between CPU and motherboard including:
 * - CPU generation vs chipset support
 * - BIOS version requirements
 * - BIOS flashback availability
 * - Out-of-box compatibility
 */

import type {
  CompatibilityChecker,
  CompatibilityResult,
  BIOSCompatibility,
  CompatibilityContext
} from '../types/CompatibilityResult'
import type { CPUModel } from '../../models/cpu/cpu-model'
import type { MotherboardModel } from '../../models/motherboard/motherboard-model'

/**
 * BIOS Compatibility Checker
 */
export class BIOSCompatibilityChecker implements CompatibilityChecker<CPUModel, MotherboardModel> {
  check(cpu: CPUModel, motherboard: MotherboardModel, _context?: CompatibilityContext): CompatibilityResult[] {
    const results: CompatibilityResult[] = []

    // BIOS version compatibility check
    const biosVersionResult = this.checkBIOSVersionCompatibility(cpu, motherboard)
    results.push(biosVersionResult)

    // Out-of-box compatibility check
    const outOfBoxResult = this.checkOutOfBoxCompatibility(cpu, motherboard)
    if (outOfBoxResult) results.push(outOfBoxResult)

    // BIOS flashback capability check
    const flashbackResult = this.checkBIOSFlashbackCapability(cpu, motherboard)
    if (flashbackResult) results.push(flashbackResult)

    return results
  }

  getScore(cpu: CPUModel, motherboard: MotherboardModel): number {
    const biosInfo = this.analyzeBIOSCompatibility(cpu, motherboard)

    if (biosInfo.isSupported && !biosInfo.updateRequired) return 100
    if (biosInfo.isSupported && biosInfo.updateRequired && biosInfo.flashbackSupported) return 85
    if (biosInfo.isSupported && biosInfo.updateRequired && !biosInfo.flashbackSupported) return 70
    return 40
  }

  private checkBIOSVersionCompatibility(cpu: CPUModel, motherboard: MotherboardModel): CompatibilityResult {
    const biosInfo = this.analyzeBIOSCompatibility(cpu, motherboard)

    if (!biosInfo.isSupported) {
      return {
        level: 'critical',
        category: 'bios',
        message: 'CPUがマザーボードのBIOSでサポートされていません',
        details: `${cpu.manufacturer} ${cpu.model} は ${motherboard.chipset} チップセットと互換性がありません`,
        solutions: [
          '互換性のあるマザーボードを選択する',
          'より新しいチップセットのマザーボードを検討する',
          'CPUを対応世代に変更する'
        ],
        score: 0
      }
    }

    if (biosInfo.updateRequired) {
      const riskLevel = this.assessUpdateRisk(cpu, motherboard, biosInfo)

      return {
        level: riskLevel === 'high' ? 'critical' : 'warning',
        category: 'bios',
        message: `CPUサポートにはBIOSアップデートが必要です`,
        details: `必要バージョン: ${biosInfo.requiredVersion ?? '不明'}, フラッシュバック対応: ${biosInfo.flashbackSupported ? 'あり' : 'なし'}`,
        solutions: biosInfo.flashbackSupported ? [
          'BIOSフラッシュバック機能を使用してアップデート',
          'CPUなしでもBIOSアップデート可能',
          'マザーボードメーカーの公式手順に従う'
        ] : [
          '対応CPUを一時的に使用してBIOSアップデート',
          'PC工房等でBIOSアップデートサービスを利用',
          'より新しいマザーボードの購入を検討'
        ],
        score: this.getScore(cpu, motherboard)
      }
    }

    return {
      level: 'info',
      category: 'bios',
      message: 'BIOS互換性に問題はありません',
      details: '出荷時BIOSでCPUがサポートされています',
      score: this.getScore(cpu, motherboard)
    }
  }

  private checkOutOfBoxCompatibility(cpu: CPUModel, motherboard: MotherboardModel): CompatibilityResult | null {
    const biosInfo = this.analyzeBIOSCompatibility(cpu, motherboard)

    if (!biosInfo.updateRequired) {
      return {
        level: 'info',
        category: 'bios',
        message: '開封時からすぐに使用可能',
        details: 'BIOSアップデートなしで動作します'
      }
    }

    return null
  }

  private checkBIOSFlashbackCapability(cpu: CPUModel, motherboard: MotherboardModel): CompatibilityResult | null {
    const biosInfo = this.analyzeBIOSCompatibility(cpu, motherboard)

    if (biosInfo.updateRequired) {
      if (biosInfo.flashbackSupported) {
        return {
          level: 'info',
          category: 'bios',
          message: 'BIOSフラッシュバック機能が利用可能',
          details: 'CPUなしでBIOSをアップデートできます',
          solutions: [
            'USBメモリにBIOSファイルを保存',
            '電源とマザーボードのみで更新可能',
            '専用ボタンを使用してフラッシュバック実行'
          ]
        }
      } else {
        return {
          level: 'warning',
          category: 'bios',
          message: 'BIOSフラッシュバック機能は非対応',
          details: 'BIOSアップデートには対応CPUが必要',
          solutions: [
            '旧世代CPUを一時的に借用',
            'PC販売店でのBIOSアップデートサービス利用',
            'より新しいマザーボードへの変更検討'
          ]
        }
      }
    }

    return null
  }

  private analyzeBIOSCompatibility(cpu: CPUModel, motherboard: MotherboardModel): BIOSCompatibility {
    // Fallback to chipset-based compatibility assessment
    // (biosInfo was removed in Phase 3 restructure)
    return this.assessChipsetCompatibility(cpu, motherboard)
  }

  private assessChipsetCompatibility(cpu: CPUModel, motherboard: MotherboardModel): BIOSCompatibility {
    // Extract CPU family from series (e.g., "Core i9" -> estimate generation)
    const cpuFamily = this.extractCPUFamily(cpu)
    const chipset = motherboard.chipset

    // Intel compatibility matrix
    if (cpu.manufacturer === 'Intel') {
      return this.assessIntelCompatibility(cpuFamily, chipset)
    }

    // AMD compatibility matrix
    if (cpu.manufacturer === 'AMD') {
      return this.assessAMDCompatibility(cpuFamily, chipset)
    }

    // Default fallback
    return {
      isSupported: true,
      updateRequired: false,
      flashbackSupported: false,
      riskLevel: 'medium'
    }
  }

  private assessIntelCompatibility(cpuFamily: string, chipset: string): BIOSCompatibility {
    const compatibilityMatrix: Record<string, {
      supportedChipsets: string[],
      updateRequired: string[],
      flashbackCommon: boolean
    }> = {
      'Raptor Lake': {
        supportedChipsets: ['Z790', 'Z690', 'B760', 'B660', 'H770', 'H670'],
        updateRequired: ['Z690', 'B660', 'H670'],
        flashbackCommon: true
      },
      'Alder Lake': {
        supportedChipsets: ['Z790', 'Z690', 'B760', 'B660', 'H770', 'H670'],
        updateRequired: [],
        flashbackCommon: true
      },
      'Rocket Lake': {
        supportedChipsets: ['Z590', 'Z490', 'B560', 'B460', 'H570', 'H470'],
        updateRequired: ['Z490', 'B460', 'H470'],
        flashbackCommon: false
      }
    }

    const compat = compatibilityMatrix[cpuFamily]
    if (!compat) {
      return { isSupported: false, updateRequired: false, flashbackSupported: false, riskLevel: 'high' }
    }

    const isSupported = compat.supportedChipsets.some(supported => chipset.includes(supported))
    const updateRequired = compat.updateRequired.some(requiresUpdate => chipset.includes(requiresUpdate))

    return {
      isSupported,
      updateRequired,
      flashbackSupported: compat.flashbackCommon,
      riskLevel: updateRequired ? 'medium' : 'low'
    }
  }

  private assessAMDCompatibility(cpuFamily: string, chipset: string): BIOSCompatibility {
    const compatibilityMatrix: Record<string, {
      supportedChipsets: string[],
      updateRequired: string[],
      flashbackCommon: boolean
    }> = {
      'Zen 4': {
        supportedChipsets: ['X670E', 'X670', 'B650E', 'B650'],
        updateRequired: [],
        flashbackCommon: true
      },
      'Zen 3': {
        supportedChipsets: ['X670E', 'X670', 'B650E', 'B650', 'X570', 'B550', 'X470', 'B450'],
        updateRequired: ['X470', 'B450'],
        flashbackCommon: true
      },
      'Zen 2': {
        supportedChipsets: ['X570', 'B550', 'X470', 'B450', 'X370', 'B350'],
        updateRequired: ['X470', 'B450', 'X370', 'B350'],
        flashbackCommon: false
      }
    }

    const compat = compatibilityMatrix[cpuFamily]
    if (!compat) {
      return { isSupported: false, updateRequired: false, flashbackSupported: false, riskLevel: 'high' }
    }

    const isSupported = compat.supportedChipsets.some(supported => chipset.includes(supported))
    const updateRequired = compat.updateRequired.some(requiresUpdate => chipset.includes(requiresUpdate))

    return {
      isSupported,
      updateRequired,
      flashbackSupported: compat.flashbackCommon,
      riskLevel: updateRequired ? 'medium' : 'low'
    }
  }

  private assessUpdateRisk(cpu: CPUModel, motherboard: MotherboardModel, biosInfo: BIOSCompatibility): 'low' | 'medium' | 'high' {
    if (!biosInfo.updateRequired) return 'low'
    if (biosInfo.flashbackSupported) return 'medium'

    // High risk if no flashback and significant generation gap
    const isNewGenOnOldBoard = this.isSignificantGenerationGap(cpu, motherboard)
    return isNewGenOnOldBoard ? 'high' : 'medium'
  }

  private isSignificantGenerationGap(cpu: CPUModel, motherboard: MotherboardModel): boolean {
    // Check if CPU is significantly newer than motherboard chipset
    // releaseDate removed in Phase 4 restructure, estimate from CPU family
    const cpuFamily = this.extractCPUFamily(cpu)
    const cpuYear = this.estimateCPUYear(cpuFamily)
    const chipsetYear = this.estimateChipsetYear(motherboard.chipset)

    return cpuYear - chipsetYear > 1
  }

  private estimateCPUYear(cpuFamily: string): number {
    const yearMap: Record<string, number> = {
      'Raptor Lake': 2022,
      'Alder Lake': 2021,
      'Rocket Lake': 2021,
      'Comet Lake': 2020,
      'Zen 4': 2022,
      'Zen 3': 2020,
      'Zen 2': 2019
    }
    return yearMap[cpuFamily] || 2023
  }

  private estimateChipsetYear(chipset: string): number {
    // Rough estimation based on chipset naming conventions
    const intelYearMap: Record<string, number> = {
      'Z790': 2022, 'Z690': 2021, 'Z590': 2021, 'Z490': 2020,
      'B760': 2023, 'B660': 2022, 'B560': 2021, 'B460': 2020
    }

    const amdYearMap: Record<string, number> = {
      'X670': 2022, 'B650': 2022, 'X570': 2019, 'B550': 2020,
      'X470': 2018, 'B450': 2018, 'X370': 2017, 'B350': 2017
    }

    return intelYearMap[chipset] || amdYearMap[chipset] || 2020
  }

  private extractCPUFamily(cpu: CPUModel): string {
    // Extract CPU family from model name
    const model = cpu.model.toLowerCase()

    // Intel detection
    if (cpu.manufacturer === 'Intel') {
      if (model.includes('13') || model.includes('13th')) return 'Raptor Lake'
      if (model.includes('12') || model.includes('12th')) return 'Alder Lake'
      if (model.includes('11') || model.includes('11th')) return 'Rocket Lake'
      if (model.includes('10') || model.includes('10th')) return 'Comet Lake'
    }

    // AMD detection
    if (cpu.manufacturer === 'AMD') {
      if (model.includes('7000') || model.includes('7950') || model.includes('7900')) return 'Zen 4'
      if (model.includes('5000') || model.includes('5950') || model.includes('5900')) return 'Zen 3'
      if (model.includes('3000') || model.includes('3950') || model.includes('3900')) return 'Zen 2'
    }

    // Default fallback
    return cpu.series || 'Unknown'
  }
}

/**
 * BIOS Compatibility Engine
 * Coordinates all BIOS compatibility checks
 */
export class BIOSCompatibilityEngine {
  private biosChecker = new BIOSCompatibilityChecker()

  checkBIOSCompatibility(cpu: CPUModel, motherboard: MotherboardModel, context?: CompatibilityContext): CompatibilityResult[] {
    return this.biosChecker.check(cpu, motherboard, context)
  }

  getBIOSCompatibilityScore(cpu: CPUModel, motherboard: MotherboardModel): number {
    return this.biosChecker.getScore(cpu, motherboard)
  }

  generateBIOSReport(cpu: CPUModel, motherboard: MotherboardModel): {
    compatible: boolean
    score: number
    updateRequired: boolean
    flashbackAvailable: boolean
    riskLevel: 'low' | 'medium' | 'high'
    instructions: string[]
    results: CompatibilityResult[]
  } {
    const results = this.checkBIOSCompatibility(cpu, motherboard)
    const score = this.getBIOSCompatibilityScore(cpu, motherboard)

    // Extract key information from results
    let updateRequired = false
    let flashbackAvailable = false
    let riskLevel: 'low' | 'medium' | 'high' = 'low'
    const instructions: string[] = []

    results.forEach(result => {
      if (result.message.includes('BIOSアップデート')) {
        updateRequired = true
      }
      if (result.message.includes('フラッシュバック')) {
        flashbackAvailable = result.message.includes('利用可能')
      }
      if (result.level === 'critical') {
        riskLevel = 'high'
      } else if (result.level === 'warning' && riskLevel !== 'high') {
        riskLevel = 'medium'
      }
      if (result.solutions) {
        instructions.push(...result.solutions)
      }
    })

    return {
      compatible: score > 60,
      score,
      updateRequired,
      flashbackAvailable,
      riskLevel,
      instructions: [...new Set(instructions)], // Remove duplicates
      results
    }
  }
}